import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Eye, FileText, Pencil, Send, Ban, Search, RotateCcw, FileSpreadsheet } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Segmented, StatusPill, TableShell } from '../ds';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';

export const STATUT_LABELS = {
  brouillon: 'Brouillon', soumise: 'À traiter', envoyee: 'Envoyée agenceur', retour: 'Visuel reçu',
  modifs: 'Modifications demandées', validee: 'Validée', annulee: 'Annulée',
};
const FILTERS = {
  all: () => true,
  encours: (d) => ['brouillon', 'soumise', 'modifs'].includes(d.statut),
  envoyee: (d) => ['envoyee', 'retour', 'validee'].includes(d.statut),
  annulee: (d) => d.statut === 'annulee',
};

const fmt = (v) => (v ? new Date(v).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—');
const agLabel = (a) => (a ? [a.societe, a.contact].filter(Boolean).join(' — ') : '—');
const titre = (d) => [d.enseigne, d.ville].filter(Boolean).join(' ') || 'Sans titre';

const DateCell = ({ at, by, testid }) => (
  <td className="px-3 py-3 align-top" data-testid={testid}>
    <div className="text-[13px] text-ink tabular-nums">{fmt(at)}</div>
    {at && by && <div className="text-[11px] text-text-3">par {by}</div>}
  </td>
);

const IconBtn = ({ icon: Icon, label, onClick, testid, danger, disabled }) => (
  <button type="button" title={label} aria-label={label} onClick={onClick} disabled={disabled} data-testid={testid}
    className={`w-8 h-8 inline-flex items-center justify-center rounded-ctrl border border-border transition-colors disabled:opacity-30 ${danger ? 'text-red-700 hover:bg-red-50' : 'text-ink hover:bg-surface-2'}`}>
    <Icon className="w-4 h-4" />
  </button>
);

function SuiviRow({ d, ag, userName, actions }) {
  const cancelled = d.statut === 'annulee';
  const firstSent = d.firstSentAt || d.sentAt;
  const resent = (d.sendCount || 0) > 1 ? d.lastSentAt : null;
  return (
    <tr className="border-t border-border hover:bg-surface-2/50" data-testid={`suivi-row-${d.id}`}>
      <td className="px-3 py-3 align-top">
        <div className="text-[13px] font-semibold text-ink">{d.ref} · IND {d.indice}</div>
        <div className="text-xs text-text-3 truncate max-w-[220px]">{titre(d)} · {d.typeContrat}</div>
      </td>
      <td className="px-3 py-3 align-top text-[13px] text-ink">{userName(d.demandeurId) || d.createdByName || '—'}</td>
      <td className="px-3 py-3 align-top text-[13px] text-ink">{agLabel(ag)}</td>
      <td className="px-3 py-3 align-top"><StatusPill status={STATUT_LABELS[d.statut] || d.statut} /></td>
      <DateCell at={d.submittedAt || d.createdAt} by={d.createdByName} testid={`suivi-created-${d.id}`} />
      <DateCell at={firstSent} by={null} testid={`suivi-firstsent-${d.id}`} />
      <DateCell at={resent} by={d.lastSentBy} testid={`suivi-resent-${d.id}`} />
      <DateCell at={d.updatedAt} by={d.updatedByName || userName(d.updatedBy)} testid={`suivi-updated-${d.id}`} />
      <td className="px-3 py-3 align-top">
        <div className="flex gap-1.5 justify-end">
          <IconBtn icon={Eye} label="Consulter le dossier et le journal" onClick={() => actions.view(d)} testid={`suivi-view-${d.id}`} />
          <IconBtn icon={FileText} label="Voir le PDF" onClick={() => actions.pdf(d)} testid={`suivi-pdf-${d.id}`} />
          <IconBtn icon={Pencil} label="Modifier" onClick={() => actions.edit(d)} testid={`suivi-edit-${d.id}`} />
          <IconBtn icon={Send} label="Renvoyer à un agenceur" onClick={() => actions.resend(d)} testid={`suivi-resend-${d.id}`} />
          {cancelled
            ? <IconBtn icon={RotateCcw} label="Réactiver la demande" onClick={() => actions.reactivate(d)} testid={`suivi-reactivate-${d.id}`} />
            : <IconBtn icon={Ban} label="Annuler la demande" danger onClick={() => actions.cancel(d)} testid={`suivi-cancel-${d.id}`} />}
        </div>
      </td>
    </tr>
  );
}

function DossierSheet({ d, ag, userName, onClose }) {
  const { api } = useAuth();
  const [journal, setJournal] = useState([]);
  useEffect(() => {
    if (!d) return;
    setJournal([]);
    api().get(`/croquis/demandes/${d.id}/journal`).then((r) => setJournal(r.data.items || [])).catch(() => {});
  }, [d, api]);
  const info = d ? [
    ['Projet', `${titre(d)} — ${d.typeContrat || ''}`], ['Adresse', [d.adresse, d.cp, d.ville].filter(Boolean).join(' ')],
    ['Concept', (d.concept || []).join(', ')], ['Format', `${d.format || ''} · ${d.formatProjet || ''}`],
    ['Emprise', `${d.L} × ${d.P} cm`], ['Priorité', d.priorite], ['Demandeur', userName(d.demandeurId) || d.createdByName],
    ['Agenceur', agLabel(ag)], ['Créée le', `${fmt(d.createdAt)} par ${d.createdByName || '—'}`],
    ['Soumise le', fmt(d.submittedAt)], ['Envoi initial', fmt(d.firstSentAt || d.sentAt)],
    ['Dernier envoi', d.lastSentAt ? `${fmt(d.lastSentAt)} par ${d.lastSentBy} à ${d.lastSentTo}` : '—'],
    ['Dernière modification', `${fmt(d.updatedAt)}${d.updatedByName ? ` par ${d.updatedByName}` : ''}`],
    ...(d.statut === 'annulee' ? [['Annulée', `${fmt(d.cancelledAt)} par ${d.cancelledBy} — ${d.cancelMotif}`]] : []),
  ] : [];
  return (
    <Sheet open={!!d} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto bg-surface" data-testid="suivi-dossier-sheet">
        {d && (<>
          <SheetHeader><SheetTitle className="text-ink">{d.ref} · IND {d.indice}</SheetTitle></SheetHeader>
          <div className="mt-2 mb-5"><StatusPill status={STATUT_LABELS[d.statut] || d.statut} /></div>
          <dl className="grid grid-cols-[150px_1fr] gap-x-3 gap-y-2 text-[13px]">
            {info.map(([k, v]) => (<React.Fragment key={k}><dt className="text-text-3">{k}</dt><dd className="text-ink">{v || '—'}</dd></React.Fragment>))}
          </dl>
          <h4 className="mt-7 mb-3 text-xs font-bold uppercase tracking-wide text-text-3">Journal des actions</h4>
          <ol className="space-y-3 border-l-2 border-border pl-4" data-testid="suivi-journal">
            {journal.length === 0 && <li className="text-[13px] text-text-3">Aucune action enregistrée.</li>}
            {journal.map((j) => (
              <li key={j.id} className="relative" data-testid="suivi-journal-item">
                <span className="absolute -left-[21px] top-1.5 w-2 h-2 rounded-full bg-orange" />
                <div className="text-[13px] text-ink">{j.action}</div>
                <div className="text-[11px] text-text-3">{fmt(j.at)} · {j.by}</div>
              </li>
            ))}
          </ol>
        </>)}
      </SheetContent>
    </Sheet>
  );
}

function ResendDialog({ d, agenceurs, onClose, onDone }) {
  const { api } = useAuth();
  const [agId, setAgId] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (d) setAgId(d.agenceurId || agenceurs[0]?.id || ''); }, [d, agenceurs]);
  const submit = async () => {
    setBusy(true);
    try {
      await api().post(`/croquis/demandes/${d.id}/send`, { agenceurId: agId });
      toast.success('Demande renvoyée à l\'agenceur'); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Échec de l\'envoi'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!d} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-surface" data-testid="suivi-resend-dialog">
        <DialogHeader><DialogTitle className="text-ink">Renvoyer {d?.ref}</DialogTitle></DialogHeader>
        <p className="text-[13px] text-text-3">Le PDF (indice {d?.indice}) et les plans DWG partent tout de suite par email à l'agenceur choisi. L'indice ne change pas.</p>
        <label className="text-xs font-bold uppercase tracking-wide text-text-3 mt-2">Agenceur</label>
        <select value={agId} onChange={(e) => setAgId(e.target.value)} data-testid="suivi-resend-agenceur"
          className="w-full h-10 px-3 rounded-ctrl border border-border bg-surface text-ink text-sm">
          {agenceurs.map((a) => <option key={a.id} value={a.id}>{agLabel(a)}{a.id === d?.agenceurId ? ' (actuel)' : ''}</option>)}
        </select>
        <DialogFooter>
          <button onClick={onClose} className="h-10 px-4 rounded-ctrl border border-border text-sm text-ink" data-testid="suivi-resend-close">Fermer</button>
          <button onClick={submit} disabled={!agId || busy} data-testid="suivi-resend-confirm"
            className="h-10 px-4 rounded-ctrl bg-orange text-orange-on text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">
            <Send className="w-4 h-4" /> {busy ? 'Envoi…' : 'Renvoyer maintenant'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReactivateDialog({ d, onClose, onDone }) {
  const { api } = useAuth();
  const [busy, setBusy] = useState(false);
  const prev = STATUT_LABELS[d?.statutAvantAnnulation] || 'Brouillon';
  const submit = async () => {
    setBusy(true);
    try { await api().post(`/croquis/demandes/${d.id}/reactivate`); toast.success(`Demande réactivée (${prev})`); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Réactivation impossible'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!d} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-surface" data-testid="suivi-reactivate-dialog">
        <DialogHeader><DialogTitle className="text-ink">Réactiver {d?.ref}</DialogTitle></DialogHeader>
        <p className="text-[13px] text-text-3">La demande retrouve son statut d'avant l'annulation : <b className="text-ink">{prev}</b>. Aucun email n'est envoyé ; l'action est notée au journal.</p>
        {d?.cancelMotif && <p className="text-[13px] text-text-3">Motif d'annulation : {d.cancelMotif}</p>}
        <DialogFooter>
          <button onClick={onClose} className="h-10 px-4 rounded-ctrl border border-border text-sm text-ink" data-testid="suivi-reactivate-close">Fermer</button>
          <button onClick={submit} disabled={busy} data-testid="suivi-reactivate-confirm"
            className="h-10 px-4 rounded-ctrl bg-orange text-orange-on text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">
            <RotateCcw className="w-4 h-4" /> {busy ? 'Réactivation…' : 'Réactiver'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CancelDialog({ d, onClose, onDone }) {
  const { api } = useAuth();
  const [motif, setMotif] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (d) setMotif(''); }, [d]);
  const submit = async () => {
    setBusy(true);
    try { await api().post(`/croquis/demandes/${d.id}/cancel`, { motif }); toast.success('Demande annulée'); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Annulation impossible'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!d} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-surface" data-testid="suivi-cancel-dialog">
        <DialogHeader><DialogTitle className="text-ink">Annuler {d?.ref}</DialogTitle></DialogHeader>
        <p className="text-[13px] text-text-3">La demande reste dans l'historique avec le statut « Annulée ». L'agenceur n'est pas prévenu.</p>
        <textarea value={motif} onChange={(e) => setMotif(e.target.value)} rows={3} placeholder="Motif de l'annulation (obligatoire)"
          data-testid="suivi-cancel-motif" className="w-full px-3 py-2 rounded-ctrl border border-border bg-surface text-ink text-sm" />
        <DialogFooter>
          <button onClick={onClose} className="h-10 px-4 rounded-ctrl border border-border text-sm text-ink" data-testid="suivi-cancel-close">Fermer</button>
          <button onClick={submit} disabled={!motif.trim() || busy} data-testid="suivi-cancel-confirm"
            className="h-10 px-4 rounded-ctrl bg-red-600 text-white text-sm font-semibold disabled:opacity-50">{busy ? 'Annulation…' : 'Confirmer l\'annulation'}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CroquisAdminSuivi({ demandes, agenceurs, onReload, onEdit, pdfUrl }) {
  const { api } = useAuth();
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [users, setUsers] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [resending, setResending] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [reactivating, setReactivating] = useState(null);
  const [exporting, setExporting] = useState(false);
  const exportXlsx = async () => {
    setExporting(true);
    try {
      const r = await api().get('/croquis/suivi/export', { params: { groupe: filter, q }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `Suivi_croquis_${new Date().toISOString().slice(0, 10)}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export Excel impossible'); }
    finally { setExporting(false); }
  };
  useEffect(() => { api().get('/croquis/users').then((r) => setUsers(r.data.items || r.data || [])).catch(() => {}); }, [api]);
  const userName = (id) => users.find((u) => u.id === id)?.name;
  const agById = (id) => agenceurs.find((a) => a.id === id);
  const counts = useMemo(() => Object.fromEntries(Object.entries(FILTERS).map(([k, f]) => [k, demandes.filter(f).length])), [demandes]);
  const rows = demandes.filter(FILTERS[filter]).filter((d) => {
    const s = q.trim().toLowerCase();
    return !s || [d.ref, d.enseigne, d.ville, d.createdByName, agLabel(agById(d.agenceurId))].join(' ').toLowerCase().includes(s);
  });
  const done = (setter) => () => { setter(null); onReload(); };
  const actions = { view: setViewing, pdf: (d) => window.open(pdfUrl(d.id), '_blank'), edit: onEdit, resend: setResending, cancel: setCancelling, reactivate: setReactivating };
  const options = [['all', 'Toutes'], ['encours', 'En cours'], ['envoyee', 'Envoyées'], ['annulee', 'Annulées']]
    .map(([value, l]) => ({ value, label: `${l} (${counts[value]})` }));
  return (
    <div className="space-y-4" data-testid="croquis-admin-suivi">
      <div className="flex flex-wrap items-center gap-3">
        <Segmented options={options} value={filter} onChange={setFilter} size="sm" />
        <div className="relative ml-auto">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-text-3" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Réf., magasin, demandeur…" data-testid="suivi-search"
            className="h-9 pl-9 pr-3 w-64 rounded-ctrl border border-border bg-surface text-sm text-ink" />
        </div>
        <button onClick={exportXlsx} disabled={exporting} data-testid="suivi-export-xlsx"
          className="h-9 px-3.5 rounded-ctrl border border-border bg-surface text-sm font-semibold text-ink inline-flex items-center gap-2 hover:bg-surface-2 disabled:opacity-50">
          <FileSpreadsheet className="w-4 h-4" /> {exporting ? 'Export…' : 'Exporter Excel'}
        </button>
      </div>
      <TableShell>
        <table className="w-full min-w-[1100px] text-left">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-text-3">
              {['Demande', 'Demandeur', 'Agenceur', 'Statut', 'Créée / soumise', 'Envoi initial', 'Dernier renvoi', 'Dernière modif.', ''].map((h) => (
                <th key={h} className="px-3 py-3 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => <SuiviRow key={d.id} d={d} ag={agById(d.agenceurId)} userName={userName} actions={actions} />)}
            {rows.length === 0 && <tr><td colSpan={9} className="px-3 py-8 text-center text-sm text-text-3">Aucune demande.</td></tr>}
          </tbody>
        </table>
      </TableShell>
      <DossierSheet d={viewing} ag={viewing && agById(viewing.agenceurId)} userName={userName} onClose={() => setViewing(null)} />
      <ResendDialog d={resending} agenceurs={agenceurs} onClose={() => setResending(null)} onDone={done(setResending)} />
      <ReactivateDialog d={reactivating} onClose={() => setReactivating(null)} onDone={done(setReactivating)} />
      <CancelDialog d={cancelling} onClose={() => setCancelling(null)} onDone={done(setCancelling)} />
    </div>
  );
}
